import React from 'react';
import { StarPlayerData as StarPlayer } from '../types';

export type { StarPlayer };

interface StarPlayerModalProps {
  player: StarPlayer;
  isSelected: boolean;
  onToggleSelect: () => void;
  onClose: () => void;
  rulesetMode: 'league' | 'exhibition';
  isRegistered?: boolean;
}

// Complete set of standard Blood Bowl 2020/2025 skills & traits for dynamic highlighting
const STANDARD_SKILLS = new Set([
  'block', 'dodge', 'wrestle', 'tackle', 'strip ball', 'sure hands', 'pass', 'catch', 'safe pass',
  'on the ball', 'cannoneer', 'hail mary pass', 'leader', 'claws', 'mighty blow', 'guard', 'stand firm',
  'grab', 'juggernaut', 'break tackle', 'sprint', 'sure feet', 'sidestep', 'diving tackle', 'shadowing',
  'prehensile tail', 'tentacles', 'foul appearance', 'disturbing presence', 'regeneration', 'iron hard skin',
  'monstrous mouth', 'pick-me-up', 'thick skull', 'stunty', 'titchy', 'right stuff', 'secret weapon',
  'chainsaw', 'bomb', 'ball & chain', 'ball and chain', 'bonehead', 'really stupid', 'wild animal',
  'unchannelled fury', 'animal savagery', 'take root', 'always hungry', 'throw team-mate', 'throw teammate',
  'swoop', 'no ball', 'animosity', 'bloodlust', 'decay', 'hypnotic gaze', 'ofishul', 'lone fouler',
  'dirty player', 'sneaky git', 'loner'
]);

export const StarPlayerModal: React.FC<StarPlayerModalProps> = ({
  player,
  isSelected,
  onToggleSelect,
  onClose,
  rulesetMode,
  isRegistered
}) => {
  const formattedCost = player.Cost ? player.Cost.toLocaleString('en-US') : '0';

  const isStandardSkill = (skill: string): boolean => {
    const cleanSkill = skill.toLowerCase().split('(')[0].trim();
    return STANDARD_SKILLS.has(cleanSkill) || cleanSkill.startsWith('loner');
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.65)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1050,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#ffffff',
          borderRadius: '16px',
          padding: '28px',
          width: '90%',
          maxWidth: '480px',
          boxSizing: 'border-box',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          border: '1px solid #e2e8f0',
          position: 'relative',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
          <div>
            <div style={{ fontSize: '11px', color: '#6366f1', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>
              ⭐ Star Player Card
            </div>
            <h3 style={{ margin: 0, fontSize: '22px', fontWeight: '800', color: '#0f172a', lineHeight: 1.2 }}>
              {player.name}
            </h3>
          </div>
          <button
            onClick={onClose}
            style={{
              background: '#f1f5f9',
              border: 'none',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              fontSize: '20px',
              cursor: 'pointer',
              color: '#64748b',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.15s',
            }}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#e2e8f0')}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#f1f5f9')}
          >
            ×
          </button>
        </div>

        {/* Cost & Recruitment Banner */}
        <div
          style={{
            background: isSelected ? '#e0e7ff' : '#f8fafc',
            border: `1px solid ${isSelected ? '#c7d2fe' : '#e2e8f0'}`,
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            transition: 'all 0.2s',
          }}
        >
          <div>
            <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: '600', letterSpacing: '0.5px', marginBottom: '2px' }}>
              Draft Cost
            </div>
            <div style={{ fontWeight: '800', fontSize: '20px', color: isSelected ? '#4338ca' : '#0f172a' }}>
              {formattedCost} <span style={{ fontSize: '14px', fontWeight: 'normal', color: '#64748b' }}>gp</span>
            </div>
          </div>

          <div>
            {rulesetMode === 'exhibition' && !isRegistered ? (
              <button
                onClick={onToggleSelect}
                style={{
                  background: isSelected ? '#dc2626' : '#4f46e5',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  transition: 'background-color 0.15s',
                }}
                onMouseOver={(e) => (e.currentTarget.style.backgroundColor = isSelected ? '#b91c1c' : '#4338ca')}
                onMouseOut={(e) => (e.currentTarget.style.backgroundColor = isSelected ? '#dc2626' : '#4f46e5')}
              >
                {isSelected ? 'Dismiss Player' : 'Draft Star Player'}
              </button>
            ) : (
              <span
                style={{
                  background: '#f1f5f9',
                  border: '1px solid #cbd5e1',
                  color: '#475569',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: 'bold',
                }}
              >
                🔒 {isSelected ? 'Hired (Locked)' : 'Ref Only'}
              </span>
            )}
          </div>
        </div>

        {/* Stats Grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 1fr)',
            gap: '8px',
            marginBottom: '20px',
            textAlign: 'center',
          }}
        >
          {[
            { label: 'MA', value: player.MA, bg: '#fef2f2', color: '#991b1b', border: '#fca5a5' },
            { label: 'ST', value: player.ST, bg: '#fff7ed', color: '#9a3412', border: '#fed7aa' },
            { label: 'AG', value: player.AG, bg: '#f0fdf4', color: '#166534', border: '#bbf7d0' },
            { label: 'PA', value: player.PA, bg: '#f0f9ff', color: '#075985', border: '#bae6fd' },
            { label: 'AR', value: player.AR, bg: '#faf5ff', color: '#6b21a8', border: '#e9d5ff' },
          ].map((stat) => (
            <div
              key={stat.label}
              style={{
                background: stat.bg,
                border: `1px solid ${stat.border}`,
                borderRadius: '10px',
                padding: '8px 4px',
              }}
            >
              <div style={{ fontSize: '11px', fontWeight: 'bold', color: stat.color, opacity: 0.7, textTransform: 'uppercase' }}>
                {stat.label}
              </div>
              <div style={{ fontSize: '16px', fontWeight: '800', color: stat.color, marginTop: '2px' }}>
                {stat.value || '-'}
              </div>
            </div>
          ))}
        </div>

        {/* Skills List */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Skills & Traits
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {player.Skills && player.Skills.length > 0 ? (
              player.Skills.map((skill) => {
                const standard = isStandardSkill(skill);
                return (
                  <span
                    key={skill}
                    style={{
                      fontSize: '12px',
                      fontWeight: '600',
                      padding: '4px 10px',
                      borderRadius: '100px',
                      background: standard ? '#eff6ff' : '#fff7ed',
                      border: `1px solid ${standard ? '#bfdbfe' : '#fed7aa'}`,
                      color: standard ? '#1e40af' : '#c2410c',
                      display: 'inline-flex',
                      alignItems: 'center',
                    }}
                    title={standard ? 'Standard Rule' : 'Special Rule / Custom Trait'}
                  >
                    {!standard && '✨ '}{skill}
                  </span>
                );
              })
            ) : (
              <span style={{ fontSize: '13px', color: '#94a3b8', fontStyle: 'italic' }}>No starting skills</span>
            )}
          </div>
        </div>

        {/* Special Rule Explanation */}
        {player.SpecialRuleExplanation && (
          <div style={{ marginBottom: '24px', background: '#fffbeb', border: '1px solid #fef3c7', padding: '16px', borderRadius: '10px' }}>
            <div style={{ fontSize: '12px', fontWeight: '700', color: '#b45309', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              ⭐ {player.Skills && player.Skills.length > 0 ? player.Skills[player.Skills.length - 1] : 'Special Rule'}
            </div>
            <div style={{ fontSize: '13px', color: '#92400e', lineHeight: '1.5' }}>
              {player.SpecialRuleExplanation}
            </div>
          </div>
        )}

        {/* Plays For Section */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Plays For
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {player.plays_for && player.plays_for.length > 0 ? (
              player.plays_for.map((league) => (
                <span
                  key={league}
                  style={{
                    fontSize: '11px',
                    fontWeight: 'bold',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    background: '#f1f5f9',
                    border: '1px solid #cbd5e1',
                    color: '#475569',
                  }}
                >
                  {league}
                </span>
              ))
            ) : (
              <span style={{ fontSize: '12px', color: '#94a3b8' }}>Unknown League</span>
            )}
          </div>
        </div>

        {/* Action Button Strip */}
        <div style={{ display: 'flex', gap: '8px', marginTop: '24px' }}>
          {player.link && (
            <a
              href={player.link}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                flex: 1,
                padding: '12px',
                background: '#f8fafc',
                border: '1px solid #cbd5e1',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                color: '#475569',
                textDecoration: 'none',
                textAlign: 'center',
                boxSizing: 'border-box',
                transition: 'background-color 0.15s',
              }}
              onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#e2e8f0')}
              onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#f8fafc')}
            >
              📖 Read Rules
            </a>
          )}
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: '12px',
              background: '#0f172a',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'background-color 0.15s',
            }}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#1e293b')}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#0f172a')}
          >
            Close Card
          </button>
        </div>
      </div>
    </div>
  );
};
